import React, { useCallback, useState } from 'react';
import { getSessionKeys } from '../../../services/chat/chatKeys';
import {
  assertForSettings,
  assertionFailedThisSession,
  hasRecentAssertion,
} from '../../../services/chat/chatPasskeys';
import { isSignInFresh } from '../../../services/chat/chatReauth';
import { FreshSignIn } from './FreshSignIn';

export class SettingsGateCancelled extends Error {
  constructor() {
    super('Sign-in cancelled.');
    this.name = 'SettingsGateCancelled';
  }
}

/** Whether an error is the backend's `reauth_required` answer to a settings write. */
export function isReauthRequired(error: unknown): boolean {
  const candidate = error as { status?: number; code?: string } | undefined;
  return candidate?.status === 403 && candidate?.code === 'reauth_required';
}

export interface SettingsGate {
  /**
   * Satisfy the gate before a settings write, in the spec's order: a recent
   * assertion → nothing to do; a passkey/device/post-setup session with a
   * registered passkey and no failed assertion → assert; otherwise the fresh
   * sign-in step (rendered through `signInStep`). `force` skips the two
   * "already satisfied" shortcuts after a 403.
   */
  ensureGate: (options?: { force?: boolean }) => Promise<void>;
  /** The inline sign-in form while the gate waits on a fresh sign-in, else null. */
  signInStep: React.ReactNode;
  signingIn: boolean;
}

interface PendingSignIn {
  resolve: () => void;
  reject: (error: Error) => void;
}

export function useSettingsGate(params: { uid: string; email: string; passkeyCount: number }): SettingsGate {
  const { uid, email, passkeyCount } = params;
  const [pending, setPending] = useState<PendingSignIn | null>(null);

  const ensureGate = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!options.force && hasRecentAssertion()) return;
      const session = getSessionKeys();
      const canAssert =
        passkeyCount > 0 && !assertionFailedThisSession() && session?.unlockedWith !== 'recovery';
      if (canAssert) {
        try {
          await assertForSettings();
          return;
        } catch {
          /* cancelled or failed: fall through to the fresh sign-in step */
        }
      }
      if (!options.force && isSignInFresh()) return;
      await new Promise<void>((resolve, reject) => setPending({ resolve, reject }));
    },
    [passkeyCount],
  );

  const signInStep = pending ? (
    <FreshSignIn
      uid={uid}
      email={email}
      intro="Sign in again to change your chat encryption settings."
      onDone={() => {
        pending.resolve();
        setPending(null);
      }}
      onCancel={() => {
        pending.reject(new SettingsGateCancelled());
        setPending(null);
      }}
    />
  ) : null;

  return { ensureGate, signInStep, signingIn: pending != null };
}
